import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';
import { api, type CustomerSummary } from '../lib/api.js';
import { cn } from '../lib/cn.js';
import { Input } from './ui/input.js';
import { Pill } from './ui/pill.js';

const MIN_CHARS = 2;
const DEBOUNCE_MS = 250;
const MAX_RESULTS = 8;

/**
 * Find a customer by NAME or NUMBER. Replaces the bare "Customer #" inputs,
 * which needed the exact customer number. Backed by the API's free-text
 * customer search (Spire `?q=` — case-insensitive, multi-word), so
 * "elite home" finds "Elite Home Builders".
 *
 * Controlled by `value` (the selected customerNo, '' for none). Typing opens
 * a results list; picking a row calls `onSelect` with the full summary.
 */
export function CustomerPicker({
  value,
  valueLabel,
  onSelect,
  onClear,
  placeholder = 'Search customer name or #',
  size = 'md',
  autoFocus = false,
  id,
  className,
}: {
  value: string;
  /** Shown while a customer is selected — e.g. its name. Defaults to `value`. */
  valueLabel?: string;
  onSelect: (customer: CustomerSummary) => void;
  onClear?: () => void;
  placeholder?: string;
  /** `sm` fits the dispatch toolbar; `md` is the standard form height. */
  size?: 'sm' | 'md';
  autoFocus?: boolean;
  id?: string;
  className?: string;
}) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [text, setText] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(text.trim()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [text]);

  // Close on outside click/tap.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  const enabled = debounced.length >= MIN_CHARS;
  const results = useQuery({
    queryKey: ['customers', 'picker', debounced],
    queryFn: ({ signal }) => api.customers.search({ q: debounced, limit: MAX_RESULTS }, signal),
    enabled,
    staleTime: 60_000,
  });
  const rows = enabled ? (results.data ?? []) : [];

  useEffect(() => setActive(0), [debounced]);

  const pick = (c: CustomerSummary) => {
    onSelect(c);
    setText('');
    setDebounced('');
    setOpen(false);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      if (open) e.stopPropagation();
      setOpen(false);
      return;
    }
    if (!open || rows.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (i + 1) % rows.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (i - 1 + rows.length) % rows.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const row = rows[active];
      if (row) pick(row);
    }
  };

  const selected = value.length > 0 && text.length === 0;
  const sm = size === 'sm';

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <Search
        className={cn(
          'absolute top-1/2 -translate-y-1/2 text-ink-subtle pointer-events-none',
          sm ? 'left-2 h-3.5 w-3.5' : 'left-3 h-4 w-4',
        )}
        aria-hidden
      />
      <Input
        id={id}
        role="combobox"
        aria-label="Customer name or number"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        placeholder={selected ? (valueLabel ?? value) : placeholder}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
        }}
        onFocus={() => text.trim().length >= MIN_CHARS && setOpen(true)}
        onKeyDown={onKeyDown}
        autoFocus={autoFocus}
        autoCorrect="off"
        autoComplete="off"
        spellCheck={false}
        className={cn(
          sm ? 'h-8 pl-7 pr-7 text-xs' : 'pl-9 pr-9',
          // A selected customer renders via the placeholder so typing replaces it.
          selected && 'placeholder:text-ink placeholder:font-medium',
        )}
      />
      {(selected || text.length > 0) && (
        <button
          type="button"
          aria-label={selected ? 'Clear customer' : 'Clear search'}
          onClick={() => {
            setText('');
            setOpen(false);
            if (selected) onClear?.();
          }}
          className={cn(
            'absolute top-1/2 -translate-y-1/2 inline-flex items-center justify-center rounded text-ink-subtle hover:text-ink',
            sm ? 'right-1 h-6 w-6' : 'right-1.5 h-8 w-8',
          )}
        >
          <X className={sm ? 'h-3.5 w-3.5' : 'h-4 w-4'} aria-hidden />
        </button>
      )}

      {open && text.trim().length >= MIN_CHARS && (
        <ul
          id={listId}
          role="listbox"
          className={cn(
            'absolute z-40 mt-1 max-h-80 overflow-auto rounded-lg border bg-surface shadow-lg',
            sm ? 'right-0 w-72' : 'left-0 right-0',
          )}
          style={{ borderColor: 'var(--color-border-secondary)' }}
        >
          {!enabled || results.isPending ? (
            <li className="px-3 py-2 text-sm text-ink-muted">Searching…</li>
          ) : results.isError ? (
            <li className="px-3 py-2 text-sm text-red-700">Search failed — try again.</li>
          ) : rows.length === 0 ? (
            <li className="px-3 py-2 text-sm text-ink-muted">No customers match “{debounced}”.</li>
          ) : (
            rows.map((c, i) => (
              <li key={c.spireId} role="option" aria-selected={i === active}>
                <button
                  type="button"
                  // pointerdown-outside closes the list; keep the click inside it.
                  onMouseEnter={() => setActive(i)}
                  onClick={() => pick(c)}
                  className={cn(
                    'flex w-full items-center gap-2 px-3 py-2 text-left',
                    i === active && 'bg-surface-muted',
                  )}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-ink">{c.name}</span>
                    <span className="block truncate text-xs text-ink-muted">
                      <span className="font-mono">{c.customerNo}</span>
                      {c.address.city ? ` · ${c.address.city}` : ''}
                    </span>
                  </span>
                  {c.hold && <Pill variant="danger">on hold</Pill>}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
